import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Breadcrumb from '../Pages/Breadcrumb';
import Image from '../ui/Image';
import NewsLoading from '../loading/news-loading';
import { useLanguage } from '../../store/language-context';
import { useTranslation } from 'react-i18next';

const News = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const { URL } = useLanguage();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['allNews', currentPage, URL],
    queryFn: async () => {
      const response = await axios.get(`${URL}/latestNews?page=${currentPage}`);

      if (response.status === 200) {
        return response?.data;
      } else {
        throw new Error(t('custom.err'));
      }
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const news = data?.data?.data || [];
  const meta = data?.meta || {};
  const links = data?.links || {};
  const lastPage = meta?.last_page || 1;
  const prevPageUrl = links?.prev;
  const nextPageUrl = links?.next;

  const pageChangeFn = (pageNumber) => {
    if (pageNumber !== currentPage && pageNumber >= 1 && pageNumber <= lastPage) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  const paginationNumbers = () => {
    const numbers = [];
    for (let i = 1; i <= lastPage; i++) {
      if (
        i <= 5 ||
        i === currentPage ||
        i === lastPage ||
        (i === currentPage - 1 && currentPage > 3) ||
        (i === currentPage + 1 && currentPage < lastPage)
      ) {
        numbers.push(i);
      }
    }

    const paginationWithDots = [];
    for (let i = 0; i < numbers.length; i++) {
      paginationWithDots.push(numbers[i]);
      if (i < numbers.length - 1 && numbers[i + 1] - numbers[i] > 1) {
        paginationWithDots.push("...");
      }
    }
    return paginationWithDots;
  };

  if (error) {
    return (
      <div className="container">
        <div className="latest-news-container">
          <div className="text-center py-8">
            <p className="text-red-500">
              {t('custom.err')}
            </p>
            <button
              className="mt-2 bg-secondary-tw text-white px-4 py-2 rounded-md"
              onClick={() => refetch()}
            >
              {t('custom.try-again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb pages={[t('routes.news')]} />
      <main className="container">
        <section className='space-y-4'>

          <section className="grid grid-cols-3 gap-4 max-2xl:grid-cols-2 max-lg:grid-cols-1 max-lg:px-2">
            {isLoading ? (
              <NewsLoading />
            ) : news?.length > 0 ? (
              news?.map((item) => (
                <div key={item?.id} className="w-full h-full">
                  <Link to={`/news/${item?.id}`}>
                    <div className='relative w-full h-60'>
                      <Image
                        src={item?.image}
                        alt={item?.title}
                        className="rounded-md object-cover w-full h-full"
                      />
                    </div>
                    <p className='text-base my-2 text-primary-tw font-semibold'>{item?.title}</p>
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-center">
                {t('custom.no-data')}
              </p>
            )}
          </section>

          {lastPage > 1 && (
            <div className="flex gap-3 justify-center items-center my-6">
              {/* Previous Button */}
              <button
                className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!prevPageUrl}
                onClick={() => pageChangeFn(currentPage - 1)}
              >
                <ArrowRight size={20} color="#e08b3d" fill="#e08b3d" />
              </button>

              {/* Pagination Numbers */}
              {paginationNumbers().map((num, index) => (
                <button
                  key={index}
                  className={`mx-1 text-base ${num === currentPage
                    ? "text-secondary-tw border-secondary-tw border-2 px-2"
                    : "hover:text-secondary-tw"
                    } rounded-md ${num === "..." ? "cursor-default" : "cursor-pointer"}`}
                  onClick={() => num !== "..." && pageChangeFn(num)}
                  disabled={num === "..."}
                >
                  {num}
                </button>
              ))}

              {/* Next Button */}
              <button
                className="border-secondary-tw border-2 px-2 py-1 hover:ring-1 hover:ring-yellow-200 cursor-pointer rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!nextPageUrl}
                onClick={() => pageChangeFn(currentPage + 1)}
              >
                <ArrowLeft size={20} color="#e08b3d" fill="#e08b3d" />
              </button>
            </div>
          )}
        </section>
      </main>
    </>
  );
};

export default React.memo(News);